import React, { useState, useEffect } from 'react';
import { Settings, Power, Eye, EyeOff, Mail, FileText, Save, Loader2, AlertTriangle, Monitor, Smartphone, Download, HardDrive } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';
import MaintenanceMode from '../../components/MaintenanceMode';

const MaintenanceSettings = () => {
  const [settings, setSettings] = useState({
    maintenance_mode: false,
    maintenance_title: 'Wir arbeiten gerade an etwas Neuem!',
    maintenance_message: 'Unser Shop wird gerade überarbeitet. Wir sind bald wieder für Sie da.',
    show_contact_email: true,
    contact_email: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [backupPreview, setBackupPreview] = useState(null);
  const [backupBusy, setBackupBusy] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchBackupPreview();
  }, []);

  const fetchBackupPreview = async () => {
    try {
      const r = await axiosInstance.get('/admin/backup/preview');
      setBackupPreview(r.data);
    } catch (e) {
      // Silently fail — Backup ist optional
    }
  };

  const downloadBackup = async () => {
    try {
      setBackupBusy(true);
      const res = await axiosInstance.get('/admin/backup/download', { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 16).replace(/[T:]/g, '-');
      a.href = url;
      a.download = `homeyoursoul-backup-${stamp}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Backup heruntergeladen! Speichere die Datei sicher.');
    } catch (e) {
      toast.error('Backup fehlgeschlagen. Bitte erneut versuchen.');
    } finally {
      setBackupBusy(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axiosInstance.get('/settings/maintenance');
      setSettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Error fetching maintenance settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axiosInstance.put('/settings/maintenance', settings);
      toast.success(settings.maintenance_mode 
        ? 'Wartungsmodus aktiviert! Die Seite ist jetzt offline.' 
        : 'Wartungsmodus deaktiviert! Die Seite ist jetzt online.');
    } catch (error) {
      console.error('Error saving maintenance settings:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const toggleMaintenanceMode = async (enabled) => {
    setSettings(prev => ({ ...prev, maintenance_mode: enabled }));
    
    setSaving(true);
    try {
      await axiosInstance.put('/settings/maintenance', {
        ...settings,
        maintenance_mode: enabled
      });
      try { sessionStorage.removeItem('maintenanceSettings'); } catch (_e) { /* ignore */ }
      toast.success(enabled 
        ? '🔴 Seite ist jetzt OFFLINE' 
        : '🟢 Seite ist jetzt ONLINE');
    } catch (error) {
      console.error('Error toggling maintenance mode:', error);
      toast.error('Fehler beim Umschalten');
      setSettings(prev => ({ ...prev, maintenance_mode: !enabled }));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#8B7355]" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold text-[#5D4E3C] mb-6 flex items-center gap-2">
        <Settings className="h-6 w-6" />
        Wartungsmodus
      </h1>

      {/* Backup-Sektion — prominent oben */}
      <div className="bg-white border-2 border-[#8B6F4D] p-4 sm:p-6 mb-6" data-testid="backup-section">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-12 h-12 shrink-0 flex items-center justify-center bg-[#8B6F4D]">
            <HardDrive className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-[#5D4E3C]">Datenbank-Backup</h2>
            <p className="text-xs text-gray-600">
              Lade jederzeit ein komplettes Backup aller wichtigen Daten als ZIP-Datei herunter.
              Speichere sie sicher auf deinem Rechner oder in der Cloud.
            </p>
          </div>
        </div>

        {backupPreview && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4 text-center text-xs">
            <div className="bg-[#FAF5EE] border border-[#D4C4B0] p-2">
              <p className="text-lg font-bold text-[#5D4E3C]" data-testid="backup-count-orders">{backupPreview.counts?.orders ?? 0}</p>
              <p className="text-gray-600 uppercase tracking-wide text-[10px]">Bestellungen</p>
            </div>
            <div className="bg-[#FAF5EE] border border-[#D4C4B0] p-2">
              <p className="text-lg font-bold text-[#5D4E3C]" data-testid="backup-count-invoices">{backupPreview.counts?.invoices ?? 0}</p>
              <p className="text-gray-600 uppercase tracking-wide text-[10px]">Rechnungen</p>
            </div>
            <div className="bg-[#FAF5EE] border border-[#D4C4B0] p-2">
              <p className="text-lg font-bold text-[#5D4E3C]" data-testid="backup-count-products">{backupPreview.counts?.products ?? 0}</p>
              <p className="text-gray-600 uppercase tracking-wide text-[10px]">Produkte</p>
            </div>
            <div className="bg-[#FAF5EE] border border-[#D4C4B0] p-2">
              <p className="text-lg font-bold text-[#5D4E3C]" data-testid="backup-count-users">{backupPreview.counts?.users ?? 0}</p>
              <p className="text-gray-600 uppercase tracking-wide text-[10px]">Kunden</p>
            </div>
          </div>
        )}

        <Button
          onClick={downloadBackup}
          disabled={backupBusy}
          className="w-full sm:w-auto rounded-none bg-[#8B6F4D] hover:bg-[#5D4E3C] text-white"
          data-testid="download-backup-btn"
        >
          {backupBusy ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Erstelle Backup...</>
          ) : (
            <><Download className="h-4 w-4 mr-2" /> Jetzt Backup herunterladen (.zip)</>
          )}
        </Button>
        <p className="text-[11px] text-[#8B7355] mt-2">
          Empfohlen: alle 2 Wochen ein frisches Backup ziehen, besonders vor riskanten Aktionen (Produkt-Sync, Test-Cleanup).
        </p>
      </div>

      {/* Main Toggle - Big and Prominent */}
      <div className={`p-4 sm:p-6 mb-6 border-2 ${settings.maintenance_mode ? 'border-red-400 bg-red-50' : 'border-green-400 bg-green-50'}`}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className={`w-14 h-14 sm:w-16 sm:h-16 shrink-0 flex items-center justify-center ${settings.maintenance_mode ? 'bg-red-500' : 'bg-green-500'}`}>
              {settings.maintenance_mode ? (
                <EyeOff className="h-7 w-7 sm:h-8 sm:w-8 text-white" />
              ) : (
                <Eye className="h-7 w-7 sm:h-8 sm:w-8 text-white" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-lg sm:text-xl font-bold text-[#5D4E3C]">
                {settings.maintenance_mode ? 'Seite ist OFFLINE' : 'Seite ist ONLINE'}
              </h2>
              <p className="text-xs sm:text-sm text-gray-600">
                {settings.maintenance_mode 
                  ? 'Besucher sehen die Wartungsseite' 
                  : 'Shop ist für alle sichtbar'}
              </p>
            </div>
          </div>
          <Button
            onClick={() => toggleMaintenanceMode(!settings.maintenance_mode)}
            disabled={saving}
            className={`w-full sm:w-auto px-4 sm:px-6 py-3 text-base sm:text-lg font-semibold rounded-none whitespace-nowrap ${
              settings.maintenance_mode 
                ? 'bg-green-600 hover:bg-green-700' 
                : 'bg-red-600 hover:bg-red-700'
            } text-white`}
          >
            {saving ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : settings.maintenance_mode ? (
              <>
                <Power className="h-5 w-5 mr-2" />
                Online schalten
              </>
            ) : (
              <>
                <Power className="h-5 w-5 mr-2" />
                Offline schalten
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Warning when offline */}
      {settings.maintenance_mode && (
        <div className="bg-amber-50 border border-amber-300 p-4 mb-6 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
          <div>
            <p className="font-semibold text-amber-800">Wartungsmodus aktiv</p>
            <p className="text-sm text-amber-700">
              Ihre Kunden sehen momentan nur die Wartungsseite. Als Admin können Sie weiterhin alle Seiten besuchen.
            </p>
          </div>
        </div>
      )}

      {/* Customization */}
      <div className="bg-white border border-[#C4A084] p-5 space-y-5">
        <h3 className="font-semibold text-[#5D4E3C] flex items-center gap-2">
          <FileText className="h-4 w-4" />
          Wartungsseite anpassen
        </h3>

        <div className="space-y-4">
          <div>
            <Label htmlFor="title" className="text-sm font-medium text-[#5D4E3C]">
              Überschrift
            </Label>
            <Input
              id="title"
              value={settings.maintenance_title}
              onChange={(e) => setSettings(prev => ({ ...prev, maintenance_title: e.target.value }))}
              placeholder="Wir arbeiten gerade an etwas Neuem!"
              className="mt-1 rounded-none"
            />
          </div>

          <div>
            <Label htmlFor="message" className="text-sm font-medium text-[#5D4E3C]">
              Nachricht
            </Label>
            <textarea
              id="message"
              value={settings.maintenance_message}
              onChange={(e) => setSettings(prev => ({ ...prev, maintenance_message: e.target.value }))}
              placeholder="Unser Shop wird gerade überarbeitet..."
              className="mt-1 w-full min-h-[100px] p-3 border border-gray-300 focus:border-[#C4A084] focus:ring-1 focus:ring-[#C4A084] outline-none"
              rows={3}
            />
          </div>

          <div className="flex items-center justify-between py-2">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-[#8B7355]" />
              <Label htmlFor="show-email" className="text-sm text-[#5D4E3C]">
                Kontakt-E-Mail anzeigen
              </Label>
            </div>
            <Switch
              id="show-email"
              checked={settings.show_contact_email}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, show_contact_email: checked }))}
            />
          </div>

          {settings.show_contact_email && (
            <div>
              <Label htmlFor="email" className="text-sm font-medium text-[#5D4E3C]">
                Kontakt-E-Mail
              </Label>
              <Input
                id="email"
                type="email"
                value={settings.contact_email}
                onChange={(e) => setSettings(prev => ({ ...prev, contact_email: e.target.value }))}
                placeholder="kontakt@homeyoursoul.com"
                className="mt-1 rounded-none"
              />
            </div>
          )}
        </div>

        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full bg-[#8B7355] hover:bg-[#7A6548] text-white rounded-none"
        >
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          Einstellungen speichern
        </Button>
      </div>

      {/* Live-Vorschau der Wartungsseite */}
      <div className="mt-6 bg-white border border-[#C4A084]" data-testid="maintenance-preview-wrapper">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E8DCC8] bg-[#FAF5EE]">
          <h3 className="font-semibold text-[#5D4E3C] flex items-center gap-2">
            <Eye className="h-4 w-4" />
            Live-Vorschau — so sehen Besucher die Seite
          </h3>
          <div className="hidden sm:flex items-center gap-2 text-xs text-[#8B7355]">
            <div className="flex items-center gap-1"><Monitor className="h-3.5 w-3.5" /> Desktop</div>
            <div className="flex items-center gap-1"><Smartphone className="h-3.5 w-3.5" /> Mobile</div>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-3 p-3">
          {/* Desktop-Vorschau */}
          <div className="border border-[#E8DCC8] overflow-hidden">
            <div className="bg-[#E8DED1] text-[10px] text-[#5D4E3C] px-2 py-1 flex items-center gap-1">
              <Monitor className="h-3 w-3" /> Desktop
            </div>
            <div className="h-[380px] overflow-auto">
              <MaintenanceMode settings={settings} embedded />
            </div>
          </div>
          {/* Mobile-Vorschau */}
          <div className="border border-[#E8DCC8] overflow-hidden mx-auto md:mx-0" style={{ maxWidth: 300 }}>
            <div className="bg-[#E8DED1] text-[10px] text-[#5D4E3C] px-2 py-1 flex items-center gap-1">
              <Smartphone className="h-3 w-3" /> iPhone
            </div>
            <div className="h-[380px] overflow-auto">
              <MaintenanceMode settings={settings} embedded />
            </div>
          </div>
        </div>
        <p className="text-[11px] text-[#8B7355] px-4 pb-3">
          Die Vorschau aktualisiert sich sofort während du Titel, Nachricht oder Kontakt-E-Mail änderst.
        </p>
      </div>
    </div>
  );
};

export default MaintenanceSettings;
